import {EphemeralRealtimeService} from '../../core/services/ephemeral-realtime.service';
import {Subscription} from 'rxjs';
import {ChangeDetectorRef, Component, NgZone, OnInit, signal, ViewChild} from '@angular/core';
import {AppComponent} from "../../app.component";
import {SiteLayoutComponent} from "../../core/layout/site/site-layout.component";
import {CalendarOptions, EventInput} from "@fullcalendar/core";
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import plLocale from '@fullcalendar/core/locales/pl';
import deLocale from '@fullcalendar/core/locales/de';
import {createEventId} from './event-utils';
import {FullCalendarComponent} from "@fullcalendar/angular";
import {UserRestService} from "../../api/services/user.rest.service";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {EventApiModel} from "../../api/models/event.api.model";
import {TranslateService} from "@ngx-translate/core";
import {AbstractComponent} from "../../core/components/abstract-component";
import {EventProviderService} from "../../features/event/event-provider.service";
import {finalize, map} from "rxjs/operators";
import {UserViewModel} from "../../features/user/settings/user.view-model";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {UserApiModel} from "../../api/models/user.api-model";
import {environment} from "../../../environments/environment";
import {MediaObjectRestService} from "../../api/services/media-object.rest.service";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {of} from "rxjs";
import {Router} from "@angular/router";

@Component({
  selector: 'app-rightmenu',
  templateUrl: './app.rightmenu.component.html'
})
export class AppRightMenuComponent extends AbstractComponent implements OnInit {
  @ViewChild(FullCalendarComponent) calendar: FullCalendarComponent

  calendarOptions = signal<CalendarOptions>({
    plugins: [
      interactionPlugin,
      dayGridPlugin,
      timeGridPlugin,
    ],
    headerToolbar: {
      left: 'today title',
      center: 'prev,next',
      right: 'dayGridMonth,timeGridDay'
    },
    buttonIcons: {
      prev: 'chevron-left',
      next: 'chevron-right',
      today: 'calendar-clock',
    },
    buttonText: this.getCalendarButtonText(),
    locale: this.getCalendarLocale(),
    firstDay: this.getCalendarFirstDay(),
    initialView: 'dayGridMonth',
    views: {
      dayGridMonth: {
        titleFormat: {year: 'numeric', month: 'long'}
      }
    },
    dateClick: (info) => {
      const calendarApi = this.calendar.getApi();
      calendarApi.changeView('timeGridDay', info.date);
    },
    weekends: true,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    // select: this.handleDateSelect.bind(this),
    // eventClick: this.handleEventClick.bind(this),
    // eventsSet: this.handleEvents.bind(this)
    /* you can update a remote database when these fire:
    eventAdd:
    eventChange:
    eventRemove:
    */
  });

  public events: EventApiModel[] = [];
  public eventInputs: EventInput[] = [];
  public onlineUsers: UserViewModel[] = [];
  public onlineUsersCount: number = 0;
  public onlineUsersLoading: boolean = false;
  public readonly onlineUsersLimit: number = 50;
  public readonly onlineUsersSkeletons: null[] = Array.from({length: 30}).map(() => null);
  public onlineUsersAvailable = false;
  private releaseRoster?: () => void;
  private rosterSubscriptions = new Subscription();
  private rosterRecords = new Map<number, UserApiModel>();
  private rosterVersion = -1;
  private rosterQueue: any[] = [];
  private rosterRequest = 0;
  private hydrationRequest = 0;
  private hydrationSubscription?: Subscription;

  constructor(
    public appMain: SiteLayoutComponent,
    public app: AppComponent,
    private readonly translate: TranslateService,
    private readonly eventDPS: EventProviderService,
    private readonly userRS: UserRestService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly realtime: EphemeralRealtimeService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.subscriptions.push(this.translate.onLangChange.subscribe(() => this.updateCalendarLocale()));
    const queryParams = new QueryParamsApiModel();
    queryParams.pagination = false;
    this.subscriptions.push(
      this.eventDPS.getMyEventsCollection(queryParams)
      .subscribe((resultListAM: ResultListApiModel<EventApiModel>) => {
        this.events = resultListAM.records;
        this.eventInputs = this.events.map((e: EventApiModel) => {
          return {
            id: createEventId(),
            title: e.name,
            start: e.eventDates[0].start,
            end: e.eventDates[0].end
          };
        })
      }));
  }

  public override ngOnDestroy(): void {
    this.closeOnlineUsersStream();
    super.ngOnDestroy();
  }

  onSidebarOpen(): void {
    this.calendar.getApi().setOption('height', '22rem');
    this.calendar.getApi().setOption('locale', this.getCalendarLocale());
    this.calendar.getApi().setOption('firstDay', this.getCalendarFirstDay());
    this.calendar.getApi().setOption('buttonText', this.getCalendarButtonText());
    this.calendar.getApi().render();
    this.registerOnlineUsersStream();
    this.fetchOnlineUsers();
  }

  onSidebarHide(): void {
    this.closeOnlineUsersStream();
  }

  private updateCalendarLocale(): void {
    const locale = this.getCalendarLocale();
    const firstDay = this.getCalendarFirstDay();
    const buttonText = this.getCalendarButtonText();
    this.calendarOptions.update((options) => ({...options, locale, firstDay, buttonText}));
    if (this.calendar) {
      this.calendar.getApi().setOption('locale', locale);
      this.calendar.getApi().setOption('firstDay', firstDay);
      this.calendar.getApi().setOption('buttonText', buttonText);
    }
  }

  private getCalendarLocale() {
    const language = (this.translate.currentLang || this.translate.defaultLang || 'en-US').toLowerCase();
    if (language.startsWith('pl')) {
      return plLocale;
    }
    if (language.startsWith('de')) {
      return deLocale;
    }
    return 'en';
  }

  private getCalendarFirstDay(): number {
    const language = (this.translate.currentLang || this.translate.defaultLang || 'en-US').toLowerCase();
    return language.startsWith('pl') || language.startsWith('de') ? 1 : 0;
  }

  private getCalendarButtonText(): {month: string; day: string} {
    return {
      month: this.translate.instant(this.windowWidth < 360 ? 'common.month_narrow' : 'common.month'),
      day: this.translate.instant(this.windowWidth < 360 ? 'common.day_narrow' : 'common.day'),
    };
  }

  private fetchOnlineUsers(): void {
    const request = ++this.rosterRequest;
    this.onlineUsersLoading = true;
    void this.realtime.snapshot().then(snapshot => {
      if (request !== this.rosterRequest || !this.releaseRoster) return;
      this.rosterRecords = new Map(snapshot.records.map(user => [Number(user.id), user]));
      this.rosterVersion = snapshot.version;
      this.onlineUsersCount = snapshot.total;
      this.onlineUsersAvailable = this.realtime.available$.value;
      this.onlineUsersLoading = false;
      const queued = this.rosterQueue; this.rosterQueue = [];
      for (const event of queued) this.applyRosterChange(event);
      this.applyOnlineUsers([...this.rosterRecords.values()], this.onlineUsersCount);
    }).catch(() => {
      if (request === this.rosterRequest) this.onlineUsersAvailable = false;
    }).finally(() => {
      if (request === this.rosterRequest) { this.onlineUsersLoading = false; this.cdr.markForCheck(); }
    });
  }

  private registerOnlineUsersStream(): void {
    this.closeOnlineUsersStream();
    this.rosterVersion = -1;
    this.releaseRoster = this.realtime.watchRoster();
    this.rosterSubscriptions = new Subscription();
    this.rosterSubscriptions.add(this.realtime.rosterChanges$.subscribe(event => this.applyRosterChange(event)));
    this.rosterSubscriptions.add(this.realtime.synchronized$.subscribe(() => this.fetchOnlineUsers()));
    this.rosterSubscriptions.add(this.realtime.available$.subscribe(available => {
      this.onlineUsersAvailable = available;
      this.cdr.markForCheck();
    }));
  }

  private applyRosterChange(event: any): void {
    if (!Number.isSafeInteger(event?.version) || !Array.isArray(event.upserts) || !Array.isArray(event.removed)) return;
    if (this.rosterVersion < 0 || this.onlineUsersLoading) { this.rosterQueue.push(event); return; }
    if (event.version <= this.rosterVersion) return;
    if (event.version !== this.rosterVersion + 1) { this.fetchOnlineUsers(); return; }
    event.removed.forEach(id => this.rosterRecords.delete(Number(id)));
    event.upserts.forEach(user => this.rosterRecords.set(Number(user.id), user));
    this.rosterVersion = event.version;
    this.onlineUsersCount = event.total;
    this.applyOnlineUsers([...this.rosterRecords.values()], event.total);
  }

  private closeOnlineUsersStream(): void {
    this.releaseRoster?.(); this.releaseRoster = undefined;
    this.rosterSubscriptions.unsubscribe();
    this.rosterRequest++; this.hydrationRequest++; this.rosterQueue = [];
    this.hydrationSubscription?.unsubscribe();
  }

  public get onlineUsersVisible(): UserViewModel[] {
    return this.onlineUsers.slice(0, this.onlineUsersLimit);
  }

  public get onlineUsersExtraCount(): number {
    return Math.max(0, this.onlineUsersCount - this.onlineUsersLimit);
  }

  public navigateToUser(user: UserViewModel): void {
    if (!user?.slug) {
      return;
    }
    this.router.navigate(['/', user.slug]);
  }

  private applyOnlineUsers(users: UserApiModel[], total?: number): void {
    const request = ++this.hydrationRequest;
    this.hydrationSubscription?.unsubscribe();
    this.hydrationSubscription = this.hydrateOnlineUsers(users).subscribe({
        next: (viewModels: UserViewModel[]) => {
          if (request !== this.hydrationRequest) return;
          this.cdr.markForCheck();
          this.onlineUsers = this.sortByLastActive(viewModels);
          this.onlineUsersCount = total ?? viewModels.length;
        },
        error: () => {
          if (request === this.hydrationRequest) this.onlineUsersAvailable = false;
        }
      });
  }

  private hydrateOnlineUsers(users: UserApiModel[]) {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    params.filters = users
      .filter((user) => typeof user.avatar === 'string')
      .map((user) => new FilterParameterApiModel('id', user.avatar));

    if (!params.filters.length) {
      return of(this.sortByLastActive(users.map((user) => new UserViewModel(user))));
    }

    return this.mediaObjectRS.getMediaObjectCollection(params).pipe(
      map((resultList) => resultList.records),
      map((mediaObjects) => users.map((user) => {
        if (typeof user.avatar === 'string') {
          const mediaObject = mediaObjects.find((media) => media['@id'] === user.avatar);
          if (mediaObject) {
            user.avatar = mediaObject;
          }
        }
        return new UserViewModel(user);
      })),
      map((viewModels) => this.sortByLastActive(viewModels))
    );
  }

  private sortByLastActive(users: UserViewModel[]): UserViewModel[] {
    return [...users].sort((a, b) => (a.username || '').localeCompare(b.username || '', 'en') || a.id - b.id);
  }
}
